<template>
  <div>
    <v-dialog v-model="opened" max-width="1366px" :retain-focus="false">
      <v-card>
        <v-form ref="invoiceForm" v-model="isFormValid" lazy-validation>
        <v-card-title>
          {{ $t("cargar_factura") }} {{ orden.folio }}
        </v-card-title>

        <v-card-text v-if="orden.facturas.length === 0">
          <!-- Fila principal de archivos de factura -->
          <v-row>
            <v-col>
              <v-file-input :label="$t('factura')" prepend-icon="mdi-file-pdf" accept="application/pdf"
                v-model="form.factura" :rules="invoiceRules" />
            </v-col>

            <v-col v-if="$store.state.user.extranjero_facturas === false">
              <v-file-input label="XML" prepend-icon="mdi-file-code" accept="application/xml,text/xml"
                v-model="form.xml" :rules="xmlRules" />
            </v-col>

            <v-col>
              <v-text-field type="text" :label="$t('numero_factura')" prepend-icon="mdi-numeric"
                v-model="form.numero" :rules="numberRules" />
            </v-col>
          </v-row>

          <!-- Soporte y contenido nacional -->
          <v-row>
            <v-col>
              <v-file-input :label="$t('soporte')" prepend-icon="mdi-file-multiple" accept="application/pdf"
                v-model="form.soporte" :rules="supportRules" />
            </v-col>

            <v-col>
              <v-file-input :label="$t('contenido_nacional')" prepend-icon="mdi-file-multiple" accept="application/pdf"
                v-model="form.contenidoNacional" :rules="nationalContentRules" />
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

        </v-card-text>
          <v-row v-if="orden.requiere_repse == true">
            <v-col cols="12">
              <RepseForm :service-url="$store.state.proveedorUrl" :locale="$i18n.locale" v-model="form.docs"
                :docs-repse="orden.docs_repse || []" />
            </v-col>
          </v-row>

        <v-card-actions>
          <v-spacer />
          <v-btn color="success" :loading="saving" :disabled="saving" @click="guardar">{{ $t("guardar") }}</v-btn>
        </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import RepseForm from './RepseForm.vue';
import {
  INVOICE_UPLOAD_LIMITS,
  PDF_MIME_TYPES,
  XML_MIME_TYPES,
  createFileSnapshot,
  fileSizeRule,
  fileTypeRule,
  requiredRule
} from '../../utils/uploadValidation';

export default {
  props: ["orden", "modal"],
  components: {
    RepseForm
  },
  data() {
    return {
      opened: this.modal,
      isFormValid: true,
      saving: false,
      form: {
        factura: null,
        xml: null,
        soporte: null,
        numero: '',
        contenidoNacional: null,
        docs: {}
      },
      errors: []
    };
  },
  computed: {
    requiresInvoiceFiles() {
      return this.orden.facturas.length === 0;
    },
    invoiceRules() {
      return this.fileRules(INVOICE_UPLOAD_LIMITS.factura, 'PDF', PDF_MIME_TYPES, this.$t('factura'));
    },
    xmlRules() {
      return this.fileRules(INVOICE_UPLOAD_LIMITS.xml, 'XML', XML_MIME_TYPES, 'XML');
    },
    supportRules() {
      return this.fileRules(INVOICE_UPLOAD_LIMITS.soporte, 'PDF', PDF_MIME_TYPES, this.$t('soporte'));
    },
    nationalContentRules() {
      return [
        fileSizeRule(INVOICE_UPLOAD_LIMITS.contenidoNacional),
        fileTypeRule(PDF_MIME_TYPES, 'PDF')
      ];
    },
    numberRules() {
      return this.requiresInvoiceFiles
        ? [value => Boolean(String(value || '').trim()) || `${this.$t('numero_factura')} es requerido.`]
        : [];
    }
  },
  watch: {
    opened(value) {
      if (!value) this.$emit("close");
    }
  },
  methods: {
    fileRules(maxBytes, extension, mimeTypes, label) {
      const rules = [fileSizeRule(maxBytes), fileTypeRule(mimeTypes, extension)];
      return this.requiresInvoiceFiles ? [requiredRule(label), ...rules] : rules;
    },
    async guardar() {
      if (!this.$refs.invoiceForm.validate()) {
        this.showWarningMessage('Revise los campos y documentos marcados antes de continuar.');
        return;
      }

      try {
        this.saving = true;
        this.openLoader();

        // Read files into memory first. This prevents Chrome from uploading a stale
        // disk reference and lets us identify files changed after selection.
        const [factura, xml, soporte, contenidoNacional] = await Promise.all([
          createFileSnapshot(this.form.factura),
          createFileSnapshot(this.form.xml),
          createFileSnapshot(this.form.soporte),
          createFileSnapshot(this.form.contenidoNacional)
        ]);
        const repseDocuments = await Promise.all(
          Object.entries(this.form.docs).map(async ([docId, file]) => [docId, await createFileSnapshot(file)])
        );

        const facturaURL = `${this.$store.state.proveedorUrl}compras/ordenes/${this.orden.id}/factura`;
        const facturaParams = new FormData();

        if (factura) facturaParams.append("factura", factura);
        if (xml) facturaParams.append("xml", xml);
        if (soporte) facturaParams.append("soporte", soporte);
        facturaParams.append("numero", this.form.numero);
        if (contenidoNacional) facturaParams.append("contenidoNacional", contenidoNacional);
        repseDocuments.forEach(([docId, file]) => facturaParams.append(`docs[${docId}]`, file));

        const facturaRequest = await this.sendRequest(facturaURL, facturaParams, "POST", true);
        if (facturaRequest.success) {
          this.showSuccessMessage(facturaRequest.message);
          this.$emit("close");
        }
      } catch (error) {
        if (error.code === 'UPLOAD_FILE_UNREADABLE') {
          this.showWarningMessage(
            `El archivo "${error.filename}" cambió o dejó de estar disponible. Selecciónelo nuevamente.`
          );
        } else {
          console.error("Error al preparar la factura:", error);
          this.showErrorMessage('No fue posible preparar los documentos para su envío.');
        }
      } finally {
        this.saving = false;
        this.closeLoader();
      }
    }
  }
};
</script>
